import { Control, Controller, useWatch } from 'react-hook-form';
import { DataRoomFormData } from '../formData';
import { Divider, FixedColumn, Column } from 'further-ui/components/Layout';
import HorizontalFormField from 'further-ui/components/HorizontalFormField';
import FormField from 'further-ui/components/FormField';
import Switch from 'further-ui/components/Switch';
import { TextField, Typography } from '@mui/material';
import { dataRoomSystemDefaultAgreementParagraphs } from 'further-ui/labels';

type Props = {
  control: Control<DataRoomFormData>;
};

const SiteTerms: React.FC<Props> = ({ control }) => {
  const useSystemDefault = useWatch({
    control,
    name: 'legalTermsSettings.useSystemDefaultAgreement',
  });

  return (
    <FixedColumn fixedWidth="xl" spacing="lg">
      <HorizontalFormField
        label="Require agreement before data room access"
        labelSize="lg"
        justifyChildren="flex-end"
        breakpoint="sm"
        extraInformation="If turned on, users will be required to agree to your
legal terms before viewing the data room page."
        size="xl"
      >
        <Controller
          control={control}
          name="legalTermsSettings.requirePreAccessAgreement"
          render={({ field }) => (
            <Switch
              edge="start"
              checked={field.value ?? false}
              onChange={field.onChange}
            />
          )}
        />
      </HorizontalFormField>
      <Divider />
      <HorizontalFormField
        label="Use system default agreement wording"
        labelSize="lg"
        breakpoint="sm"
        justifyChildren="flex-end"
        size="xl"
        tooltipSlotProps={{ tooltip: { sx: { maxWidth: 700 } } }}
        tooltip={
          <Column spacing="xs">
            <Typography fontWeight="500">
              Using our system default agreement wording will include the
              following wording:
            </Typography>
            {dataRoomSystemDefaultAgreementParagraphs.map((paragraph) => (
              <Typography key={paragraph}>{paragraph}</Typography>
            ))}
          </Column>
        }
      >
        <Controller
          control={control}
          name="legalTermsSettings.useSystemDefaultAgreement"
          render={({ field }) => (
            <Switch
              edge="start"
              checked={field.value ?? false}
              onChange={field.onChange}
            />
          )}
        />
      </HorizontalFormField>
      {!useSystemDefault && (
        <>
          <FormField label="Custom pop-up title" size="md">
            <Controller
              control={control}
              name="legalTermsSettings.customAgreementTitle"
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder='Enter pop-up title, e.g. "Agree to access terms"'
                  value={field.value}
                  size="small"
                  fullWidth
                />
              )}
            />
          </FormField>
          <FormField label="Custom pop-up legal agreement text" size="xl">
            <Controller
              control={control}
              name="legalTermsSettings.customAgreementContent"
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  value={field.value}
                  placeholder="Before you can access this page, you must agree to the following terms..."
                  fullWidth
                  multiline
                  rows={7}
                />
              )}
            />
          </FormField>
        </>
      )}
    </FixedColumn>
  );
};

export default SiteTerms;
